/**
 * The words for an item's history — one place, shared by the History log and the
 * timeline above it, so a change reads the same in both (owner, 2026-10-15).
 */
import type { AuditEntry } from '../../../../stores/prioritization/auditStore';
import { formatAltitude } from '../../../../lib/altitudeDisplay';
import { PLANNING_MATURITY_LABELS, KNOWLEDGE_STATUS_LABELS, CONFIDENCE_LEVEL_LABELS } from '../../../../types/prioritization/itemLifecycle';
import { plainEventName, plainState } from '../../../../lib/plainEventWords';
import { WORK_TYPE_LABELS, type WorkType } from '../../../../types/work-type-recipes';
import { getItemCategoryLabel } from '../../TrackCellBadge';

export const FIELD_LABELS: Record<string, string> = {
  title: 'Title',
  description: 'Description',
  itemCategory: 'Category',
  altitudeLevel: 'Altitude',
  dedupeStatus: 'Dedupe status',
  duplicateOfId: 'Duplicate of',
  parentItemId: 'Parent item',
  tags: 'Tags',
  work_type: 'Work type',
  launch_type: 'Launch type',
  investment_type: 'Investment type',
  desiredOutcome: 'Desired outcome',
  acceptanceCriteria: 'Acceptance criteria',
  attributes: 'Attributes',
  // Recorded since 2026-10-15 — every change on the item page leaves a line.
  planning_maturity: 'Planning maturity',
  knowledge_status: 'Knowledge status',
  confidence_level: 'Confidence',
  effortCapacityUnits: 'Effort (capacity units)',
  effortSource: 'Effort source',
  ownerId: 'Owner',
  delegateId: 'Delegate',
  additionalCategory: 'Additional category',
  activeCategory: 'Active category',
  triage: 'Triage',
  collection: 'Collection',
  relationshipGroup: 'Relationship group',
  relationshipGroupName: 'Group name',
  evidence: 'Evidence',
  delivery: 'Delivery',
  acceptedValue: 'Accepted value',
  exit_status: 'State',
  whyNow: 'Why it matters',
  evidenceOfSuccess: 'Success measure',
  queueId: 'Queue',
  productNodeId: 'Product',
};

/** Fields whose changes are something added or removed, not one value replacing another. */
const ADD_REMOVE_WORDS: Record<string, [added: string, removed: string]> = {
  evidence: ['Evidence added', 'Evidence removed'],
  collection: ['Added to a collection', 'Removed from a collection'],
  relationshipGroup: ['Joined a relationship group', 'Left a relationship group'],
  additionalCategory: ['Additional category added', 'Additional category removed'],
  delivery: ['Delivery recorded', 'Delivery removed'],
  acceptedValue: ['Value accepted', 'Accepted value cleared'],
};

/** Stored codes for these fields read as words: "bug_fix" → "Bug fix". */
const ENUM_LABELS: Record<string, Record<string, string>> = {
  planning_maturity: PLANNING_MATURITY_LABELS,
  knowledge_status: KNOWLEDGE_STATUS_LABELS,
  confidence_level: CONFIDENCE_LEVEL_LABELS,
};
const CODE_FIELDS = new Set(['work_type', 'launch_type', 'investment_type', 'exit_status', 'triage', 'delivery', 'effortSource', 'activeCategory', 'additionalCategory']);

/** A note that holds data (some cycle events store JSON) reads as words, never as a blob. */
function readableNote(note: string, getItemById: (id: string) => { title?: string } | null): string {
  const t = note.trim();
  if (t.startsWith('{') || t.startsWith('[')) {
    try {
      return formatFieldValue('', JSON.parse(t), getItemById);
    } catch {
      return note;
    }
  }
  return note;
}


const UUID_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
function looksLikeId(v: unknown): v is string {
  return typeof v === 'string' && UUID_REGEX.test(v);
}

export function formatFieldValue(
  field: string,
  value: unknown,
  getItemById: (id: string) => { title?: string } | null,
  nameMap?: Record<string, string>,
): string {
  if (value === undefined || value === null || value === '') return '—';
  // A cleared value is stored as { attribute, value }: read it as "impact: 3".
  if (field === 'acceptedValue' && value && typeof value === 'object') {
    const v = value as { attribute?: unknown; value?: unknown };
    if (v.attribute != null) return `${String(v.attribute)}: ${v.value == null ? '—' : String(v.value)}`;
  }
  if ((field === 'ownerId' || field === 'delegateId') && typeof value === 'string') {
    return nameMap?.[value] ?? 'Someone';
  }
  if (typeof value === 'string' && ENUM_LABELS[field]?.[value]) return ENUM_LABELS[field][value];
  if (typeof value === 'string' && CODE_FIELDS.has(field)) {
    const w = value.replace(/_/g, ' ').toLowerCase();
    return w.charAt(0).toUpperCase() + w.slice(1);
  }
  if (field === 'altitudeLevel' && typeof value === 'number') {
    return formatAltitude(value, { short: true }) ?? String(value);
  }
  if (field === 'itemCategory' && typeof value === 'string') {
    return value.charAt(0).toUpperCase() + value.slice(1).toLowerCase();
  }
  if ((field === 'parentItemId' || field === 'duplicateOfId') && looksLikeId(value)) {
    const item = getItemById(value);
    return item?.title ?? `${String(value).slice(0, 8)}…`;
  }
  if (field === 'dedupeStatus' && typeof value === 'string') {
    const v = value.toLowerCase().replace(/_/g, ' ');
    return v.charAt(0).toUpperCase() + v.slice(1);
  }
  // Never dump raw JSON at a person. Ball-custody payloads
  // ({tier, reason, holderUserId}) were rendering as a stringified blob on the
  // Lineage tab — unreadable, and it leaked internal ids into the UI.
  if (value && typeof value === 'object') {
    const v = value as Record<string, unknown>;

    const humanTier: Record<string, string> = {
      executive: 'Executive',
      middle: 'Middle',
      bottom: 'Team',
      system: 'System',
    };
    const humanReason: Record<string, string> = {
      loop_creator: 'created the loop',
      toss: 'tossed down',
      return: 'returned upward',
      split: 'split to branches',
      aggregate: 'aggregated branches',
      accept: 'accepted',
    };

    if (typeof v.tier === 'string' || typeof v.holderUserId === 'string') {
      const tier = typeof v.tier === 'string' ? (humanTier[v.tier] ?? v.tier) : null;
      const reason = typeof v.reason === 'string' ? (humanReason[v.reason] ?? v.reason.replace(/_/g, ' ')) : null;
      // No user lookup in this scope — show "Someone" rather than a raw uuid.
      // A name here would need the roster threaded through; a truncated id is
      // worse than nothing to a reader.
      const holder = typeof v.holderUserId === 'string' ? 'Holder' : null;
      const parts = [holder, reason, tier ? `(${tier})` : null].filter(Boolean);
      if (parts.length > 0) return parts.join(' · ');
    }

    // Anything else object-shaped: a short readable summary, never a blob.
    const entries = Object.entries(v).filter(([k]) => !k.startsWith('_'));
    if (entries.length === 0) return '—';
    return entries
      .slice(0, 3)
      .map(([k, val]) => `${k.replace(/([A-Z])/g, ' $1').toLowerCase().trim()}: ${typeof val === 'object' ? '…' : String(val)}`)
      .join(' · ');
  }
  return String(value);
}


/** A changed field as a person would name it: its label when we have one. */
export function plainFieldName(field: string): string {
  const camel = field.replace(/_([a-z])/g, (_, c: string) => c.toUpperCase());
  const label = FIELD_LABELS[field] ?? FIELD_LABELS[camel];
  if (label) return label;
  const words = plainState(field);
  return words.charAt(0).toUpperCase() + words.slice(1);
}

// ============================================================================
// Description builder (pure — no JSX)
// ============================================================================

/** "revenue-at-risk" → "Revenue at risk": the value a proposal or acceptance is about. */
function valueAttributeName(event: AuditEntry): string | null {
  const key = event.payload?.attributeKey;
  if (typeof key !== 'string' || !key) return null;
  const words = key.replace(/[-_]/g, ' ').replace(/([a-z])([A-Z])/g, '$1 $2').toLowerCase();
  return words.charAt(0).toUpperCase() + words.slice(1);
}

/** Field edits whose sentence already names everything; a from → to line would only repeat it. */
const SENTENCE_ONLY_FIELDS = new Set(['objectiveLink', 'goalLink', 'customTypeList', 'patternOverride']);

/**
 * What one recorded change changed, as words: a field with its old and new value, or
 * a value with no "from" (a proposed or accepted value). Shared by the History log and
 * the item journey, so the second line under a change reads the same in both.
 */
export interface ChangeLine {
  label: string;
  /** Absent for a value event, which has only the value. */
  from?: string;
  to: string;
}

/**
 * The people a change names as its value (a new owner, a delegate), so a caller can
 * look their names up and a "from → to" line reads "Ann → Raj", never "Someone".
 */
export function namedPeopleIds(events: AuditEntry[]): string[] {
  return events.flatMap((e) => {
    const d = (e.payload?.details ?? e.payload) as Record<string, unknown> | undefined;
    if (!d || (d.field !== 'ownerId' && d.field !== 'delegateId')) return [];
    return [d.from, d.to].filter((v): v is string => typeof v === 'string' && v !== '');
  });
}

export function changeLines(
  event: AuditEntry,
  getItemById: (id: string) => { title?: string } | null,
  nameMap?: Record<string, string>,
): ChangeLine[] {
  const valueEvents = ['value_proposed', 'value_proposal', 'PROPOSAL_SUBMITTED', 'value_accepted', 'value_acceptance', 'VALUE_ACCEPTED', 'value_rejected', 'value_rejection', 'VALUE_REJECTED'];
  if (valueEvents.includes(event.eventType) && event.payload) {
    const val = event.payload.acceptedValue ?? event.payload.proposedValue ?? event.payload.rejectedValue ?? null;
    const attr = valueAttributeName(event);
    if (attr && val != null) return [{ label: attr, to: String(val) }];
    return [];
  }

  if (event.changes && Object.keys(event.changes).length > 0 && event.eventType !== 'CREATE') {
    return Object.entries(event.changes).map(([key, chg]) => ({
      label: plainFieldName(key),
      from: String((chg as { from?: unknown }).from ?? '—'),
      to: String((chg as { to?: unknown }).to ?? '—'),
    }));
  }

  if (!event.changes && event.payload) {
    const d = event.payload.details as Record<string, unknown> | undefined;
    const field = (d?.field ?? event.payload.field) as string | undefined;
    const attributeKey = d?.attributeKey as string | undefined;
    const fromVal = d?.from ?? event.payload.from;
    const toVal = d?.to ?? event.payload.to;
    if (field == null || SENTENCE_ONLY_FIELDS.has(field)) return [];
    if (fromVal === undefined && toVal === undefined) return [];
    const isSingleAttribute = (field === 'attributes' || field === 'metadata') && attributeKey != null;
    if (isSingleAttribute) {
      return [{ label: String(attributeKey), from: fromVal == null ? '—' : String(fromVal), to: toVal == null ? '—' : String(toVal) }];
    }
    return [{
      label: FIELD_LABELS[field] ?? field.replace(/_/g, ' '),
      from: formatFieldValue(field, fromVal, getItemById, nameMap),
      to: formatFieldValue(field, toVal, getItemById, nameMap),
    }];
  }

  return [];
}

/** The values alone, for a line whose title already names the field: "Low → High", "was Low", "High". */
export function changeValueText(line: ChangeLine): string {
  if (line.from === undefined || line.from === '—') return line.to;
  if (line.to === '—') return `was ${line.from}`;
  return `${line.from} → ${line.to}`;
}

/** The {field, from, to} a recorded change carries, wherever the writer put it. */
function changeDetails(event: AuditEntry): { from?: unknown; to?: unknown } {
  const p = (event.payload ?? {}) as Record<string, unknown>;
  const d = p.details && typeof p.details === 'object' ? (p.details as Record<string, unknown>) : p;
  return { from: d.from, to: d.to };
}

export function buildDescription(
  event: AuditEntry,
  sessionNameMap: Record<string, string>,
  getItemById: (id: string) => { title?: string } | null,
  /** The item's own category now, for naming an altitude whose event did not record one. */
  itemCategory?: string | null,
): string {
  const resolveSessionIds = (text: string) =>
    text.replace(
      /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/gi,
      (uuid) => sessionNameMap[uuid] ?? uuid.slice(0, 8) + '…',
    );

  switch (event.eventType) {
    case 'SYNC_VALUE_SET_ASIDE': {
      // A sync with a linked system did not use this value (canon: the value that did not win is kept).
      const p = event.payload ?? {};
      const key = String(p.field ?? '');
      const label = key.startsWith('attribute:')
        ? key.slice('attribute:'.length).replace(/_/g, ' ')
        : (FIELD_LABELS[key] ?? key).toLowerCase();
      const shown = Array.isArray(p.value) ? p.value.join(', ') : p.value == null || p.value === '' ? 'nothing' : String(p.value);
      return `Set aside ${p.from}'s ${label}, “${shown}”, in the sync with ${p.systemName}: ${String(p.reason ?? '')}.`;
    }
    case 'comment_posted': {
      const where = typeof event.payload?.anchorLabel === 'string' ? ` on ${event.payload.anchorLabel}` : '';
      const kind = event.payload?.isReply ? 'Replied' : 'Commented';
      return event.note ? `${kind}${where}: “${event.note}”` : `${kind}${where}`;
    }
    case 'CREATE':
      return 'Item created';
    case 'STATE_CHANGE':
      if (event.changes?.lifecycle_state) {
        return `Moved from ${plainState(event.changes.lifecycle_state.from)} to ${plainState(event.changes.lifecycle_state.to)}`;
      }
      if (event.changes?.exit_status ?? event.changes?.detailedState) {
        const chg = event.changes.exit_status ?? event.changes.detailedState;
        return `Status changed from ${plainState(chg.from || 'active')} to ${plainState(chg.to)}`;
      }
      return 'Status changed';
    case 'PROMOTE':
      return `Added to ${event.changes?.lifecycle_state?.to ? plainState(event.changes.lifecycle_state.to) : 'the item pool'}`;
    case 'DEMOTE':
      return `Taken out of ${event.changes?.lifecycle_state?.to ? plainState(event.changes.lifecycle_state.to) : 'the item pool'}`;
    case 'UPDATE': {
      const changedFields = event.changes ? Object.keys(event.changes) : [];
      if (changedFields.length === 1) return `${plainFieldName(changedFields[0])} changed`;
      return changedFields.length ? `${changedFields.length} things changed` : 'Edited';
    }
    case 'ITEM_PROMOTED':
      return event.fromState && event.toState
        ? `Moved from ${plainState(event.fromState)} to ${plainState(event.toState)}`
        : 'Moved forward';
    case 'value_proposed':
    case 'value_proposal':
    case 'PROPOSAL_SUBMITTED': {
      const attr = valueAttributeName(event);
      return attr ? `${attr} proposed` : 'Someone proposed a value';
    }
    case 'value_accepted':
    case 'value_acceptance':
    case 'VALUE_ACCEPTED': {
      const attr = valueAttributeName(event);
      return attr ? `${attr} accepted` : 'A proposed value was accepted';
    }
    case 'value_rejected':
    case 'value_rejection':
    case 'VALUE_REJECTED': {
      const attr = valueAttributeName(event);
      return attr ? `${attr} turned down` : 'A proposed value was turned down';
    }
    case 'decision_edited':
      return event.note || 'Decision edited';
    case 'CLASSIFICATION_SET_DIRECTLY': {
      const p = (event.payload ?? {}) as Record<string, unknown>;
      const category = typeof p.category === 'string' ? plainState(p.category) : null;
      const altitude = typeof p.altitude === 'number' ? formatAltitude(p.altitude, { category: typeof p.category === 'string' ? p.category : undefined }) : null;
      const parts = [category && category.charAt(0).toUpperCase() + category.slice(1), altitude].filter(Boolean);
      return parts.length ? `Classified as ${parts.join(', ')}` : 'Classified';
    }
    case 'commitment_item_linked':
      return 'Linked to a commitment';
    case 'signal_events_responded':
      return 'Answered a signal';
    case 'item_transition':
      return event.fromState && event.toState
        ? `Moved from ${plainState(event.fromState)} to ${plainState(event.toState)}`
        : 'Status changed';
    case 'item_title_updated': {
      const toVal =
        event.payload?.to ??
        (event.payload?.details && typeof event.payload.details === 'object'
          ? (event.payload.details as Record<string, unknown>).to
          : undefined);
      return toVal != null && toVal !== ''
        ? `Renamed to “${String(toVal).slice(0, 50)}${String(toVal).length > 50 ? '…' : ''}”`
        : 'Renamed';
    }
    case 'item_tags_updated':
    case 'item_work_type_updated':
    case 'item_launch_type_updated':
    case 'item_investment_type_updated':
    case 'item_field_updated': {
      const d = event.payload?.details && typeof event.payload.details === 'object'
        ? (event.payload.details as Record<string, unknown>)
        : undefined;
      const fieldName = String(d?.field ?? event.payload?.field ?? event.eventType.replace(/^item_|_updated$/g, ''));
      const addRemove = ADD_REMOVE_WORDS[fieldName];
      if (addRemove && d) {
        if (d.from == null && d.to != null) return addRemove[0];
        if (d.from != null && d.to == null) return addRemove[1];
      }
      if (fieldName === 'triage') return 'Triaged';
      // "set" when it had no value, "cleared" when it has none now — the same words
      // the timeline uses (owner, 2026-10-15: the two must match).
      if (d && (d.from === null || d.from === undefined || d.from === '') && d.to != null && d.to !== '') return `${plainFieldName(fieldName)} set`;
      if (d && d.from != null && d.from !== '' && (d.to === null || d.to === undefined || d.to === '')) return `${plainFieldName(fieldName)} cleared`;
      return `${plainFieldName(fieldName)} changed`;
    }
    case 'item_description_updated': {
      const toVal =
        event.payload?.to ??
        (event.payload?.details && typeof event.payload.details === 'object'
          ? (event.payload.details as Record<string, unknown>).to
          : undefined);
      return toVal != null && toVal !== ''
        ? 'Description rewritten'
        : 'Description changed';
    }
    case 'group_renamed': {
      const from = event.payload?.from as string | undefined;
      const to = event.payload?.to as string | undefined;
      if (from !== undefined && to !== undefined) {
        return `Group renamed from “${from || 'Untitled'}” to “${to || 'Untitled'}”`;
      }
      return event.note || 'Group renamed';
    }
    case 'relationship_edge_added': {
      if (event.note) return event.note;
      const otherTitle = event.payload?.otherItemTitle as string | undefined;
      const role = event.payload?.role as string | undefined;
      if (role && otherTitle) return `${role}: “${otherTitle}”`;
      return 'Tied to another item';
    }
    case 'relationship_edge_dissolved':
      return event.note || 'A tie to another item was removed';
    case 'item_category_updated': {
      const toVal =
        event.payload?.to ??
        (event.payload?.details && typeof event.payload.details === 'object'
          ? (event.payload.details as Record<string, unknown>).to
          : undefined);
      if (toVal == null || toVal === '') return 'Category changed';
      const raw = String(toVal);
      // Older demo data logged a work type under this event; say what it is.
      const workType = WORK_TYPE_LABELS[raw.toLowerCase() as WorkType];
      if (workType) return `Work type set to ${workType}`;
      return `Category set to ${getItemCategoryLabel(raw) ?? raw}`;
    }
    case 'item_altitude_updated': {
      const toVal =
        event.payload?.to ??
        (event.payload?.details && typeof event.payload.details === 'object'
          ? (event.payload.details as Record<string, unknown>).to
          : undefined);
      // The category the item had then names the level; without one, the item's own category does,
      // so the history and the item's header use the same name.
      const eventCategory =
        (event.payload?.details && typeof event.payload.details === 'object'
          ? (event.payload.details as Record<string, unknown>).itemCategory as string | undefined
          : undefined) ?? itemCategory ?? undefined;
      const label =
        toVal != null && typeof toVal === 'number'
          ? formatAltitude(toVal, { category: eventCategory, short: true })
          : undefined;
      return label ? `Altitude set to ${label}` : toVal != null ? `Altitude set to ${String(toVal)}` : 'Altitude changed';
    }
    case 'item_dedupe_updated': {
      const toVal =
        event.payload?.to ??
        (event.payload?.details && typeof event.payload.details === 'object'
          ? (event.payload.details as Record<string, unknown>).to
          : undefined);
      return toVal != null && toVal !== ''
        ? `Duplicate check: ${String(toVal).replace(/_/g, ' ')}`
        : 'Duplicate check updated';
    }
    case 'item_parent_updated': {
      const toVal =
        event.payload?.to ??
        (event.payload?.details && typeof event.payload.details === 'object'
          ? (event.payload.details as Record<string, unknown>).to
          : undefined);
      if (toVal && looksLikeId(toVal)) {
        const title = getItemById(toVal)?.title;
        return title
          ? `Placed under “${title.slice(0, 40)}${title.length > 40 ? '…' : ''}”`
          : 'Placed under a different item';
      }
      return 'Placed under a different item';
    }
    // Written by the item page's Strategy and Patterns sections.
    case 'item_objective_link_changed': {
      const d = changeDetails(event);
      // Older rows store the objective's name as a plain string.
      const asLink = (v: unknown) => (typeof v === 'string' ? { objective: v, goal: null } : v);
      const to = asLink(d.to) as { objective?: string | null; goal?: string | null } | null | undefined;
      const from = asLink(d.from) as { objective?: string | null } | null | undefined;
      if (to) return `Now supports objective “${to.objective ?? 'an objective'}”${to.goal ? `, goal “${to.goal}”` : ''}`;
      return `No longer supports objective “${from?.objective ?? 'an objective'}”`;
    }
    case 'item_goal_link_changed': {
      const from = changeDetails(event).from as { objective?: string | null; goal?: string | null } | null | undefined;
      return `No longer linked to goal “${from?.goal ?? 'a goal'}”${from?.objective ? ` under “${from.objective}”` : ''}`;
    }
    case 'item_custom_type_changed': {
      const to = changeDetails(event).to as { list?: string | null; type?: string | null } | null | undefined;
      const list = to?.list ?? 'A type list';
      return to?.type ? `${list}: set to “${to.type}”` : `${list}: cleared`;
    }
    case 'item_pattern_override_changed': {
      const to = changeDetails(event).to as { part?: string; field?: string; value?: unknown; reason?: string | null } | null | undefined;
      const what = to?.field ? to.field.replace(/([a-z])([A-Z])/g, '$1 $2').toLowerCase() : 'a pattern value';
      // A cleared override (value null) hands the field back to the item's types.
      if (to && to.value == null) return `Back to its types' ${what}${to.part ? ` (${to.part})` : ''}`;
      return `Set its own ${what}${to?.part ? ` (${to.part})` : ''}, instead of its type's${to?.reason ? ` — ${to.reason}` : ''}`;
    }
    case 'item_marked_complete':
      return 'Conditions met';
    case 'item_deleted':
      return 'Item deleted';
    case 'item_split':
      return 'Split into more than one item';
    case 'item_added_to_cycle':
      return 'Added to a cycle';
    case 'item_removed_from_cycle':
      return 'Taken out of a cycle';
    case 'item_promoted':
      return event.note ? resolveSessionIds(event.note) : 'Promoted from session';
    case 'item_created':
      return 'Item created';
    case 'item_imported':
      return event.note ? resolveSessionIds(event.note) : 'Imported from whiteboard photo';
    case 'lock':
    case 'ValueLocked': {
      const lockDetail =
        event.note ??
        (event.payload && typeof (event.payload as { attributeKey?: string }).attributeKey === 'string'
          ? (event.payload as { attributeKey: string }).attributeKey
          : null);
      return lockDetail ? `Value locked: ${lockDetail}` : 'Value locked';
    }
    case 'unlock': {
      const unlockDetail =
        event.note ??
        (event.payload && typeof (event.payload as { attributeKey?: string }).attributeKey === 'string'
          ? (event.payload as { attributeKey: string }).attributeKey
          : null);
      return unlockDetail ? `Value unlocked: ${unlockDetail}` : 'Value unlocked';
    }
    case 'shaping_started':
      if (event.payload?.details?.action === 'item_created' || event.payload?.action === 'item_created')
        return 'Item created';
      return 'Preparation started';
    case 'shaping_completed':
      return 'Conditions met';
    case 'altitude_set':
      return 'Altitude saved';
    case 'dedupe_resolved':
      return 'Duplicate check settled';
    case 'attribute_schema_attached':
      return event.payload?.details &&
        typeof event.payload.details === 'object' &&
        (event.payload.details as Record<string, unknown>).attributeKey
        ? `“${(event.payload.details as Record<string, unknown>).attributeKey as string}” changed`
        : 'Attributes changed';
    // ── Cycle governance events (appear in item history when item was in the cycle) ──
    case 'ranking_completed': {
      const note = event.note ? ` (${event.note.slice(0, 60)})` : '';
      return `Ranked in a session${note}`;
    }
    case 'ranking_overridden':
      return 'Rank changed by hand';
    case 'criteria_updated':
    case 'criteria_changed':
      return 'Criteria changed';
    case 'criteria_locked':
      return 'Criteria locked';
    case 'criteria_unlocked':
      return 'Criteria unlocked';
    case 'candidate_pool_updated':
    case 'pool_modified':
      return 'Item pool changed';
    case 'ItemPoolLocked':
      return 'Item pool locked';
    case 'ItemPoolUnlocked':
      return 'Item pool unlocked';
    case 'ItemPoolCreated':
      return 'Item pool created';
    case 'value_convergence_completed':
      return 'Values agreed';
    case 'value_source_selected':
      return 'Value source chosen';
    case 'value_source_cleared':
      return 'Value source cleared — official values apply';
    case 'PublishCompleted':
      return 'Cycle results published';
    case 'PublishAttempted':
      return 'Publishing started';
    case 'FocusListArchived':
      return 'Focus list archived';
    case 'session_created':
      return event.note ? `Session created: ${event.note.slice(0, 60)}` : 'Session created';
    case 'cycle_published':
      return 'Cycle published';
    default:
      if (event.fromState && event.toState) return `Moved from ${plainState(event.fromState)} to ${plainState(event.toState)}`;
      if (event.note) return resolveSessionIds(readableNote(event.note, getItemById));
      return plainEventName(event.eventType);
  }
}
